/**
 * Project skills list page — `GET /skills`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader` + `CordioAbpPagedDataTable` + one `DeleteDialog`, with the grid owning paging,
 * search and filtering. The page's `filters` / `totalCount` state and the `loadSkills` callback
 * that refetched on every change are gone; `listVersion` is the single piece of refetch state left.
 *
 * ─── Column plan ────────────────────────────────────────────────────────────────────────────────
 * field               │ header    │ width │ cell type        │ filter
 * ────────────────────┼───────────┼───────┼──────────────────┼──────────────────────────────────────
 * name                │ Name      │  24%  │ text             │ toolbar search (`searchTerm`)
 * slug                │ Slug      │  18%  │ text             │ —
 * providerName        │ Provider  │  18%  │ text             │ popover select → `providerId` (lazy)
 * latestVersionNumber │ Version   │  12%  │ custom (`v3`)    │ —
 * createdAt           │ Uploaded  │  16%  │ date (locale)    │ —
 * isActive            │ Status    │  12%  │ custom Chip      │ status tabs → synthetic `status`
 *
 * The old table showed the same five fields plus nothing else, so nothing was dropped; `createdAt`
 * was added because a skill list is read chronologically ("which bundle did I upload last?") and
 * the DTO already carries it. `description` and `category` are left out — the description is long
 * enough to need its own space on the detail view, and category is a filter, not a scanning column.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * WHAT THE GRID DOES NOT OWN: the zip-upload dialog below uses the @coordio/ui Dialog (Radix
 * pattern). Uploading a skill is a multipart form with a provider picker, a file input and a
 * category — all migrated off MUI onto @coordio/ui + Tailwind.
 *
 * No column is `sortable`: `skillService.getSkills` builds its query string from an explicit
 * allow-list (`pageNumber`, `pageSize`, `searchTerm`, `category`, `isActive`, `providerId`) and has
 * nowhere to put `sortBy` / `sortDescending`, so a sort header would silently do nothing.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Button,
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { EmptyState } from '@coordio/ui/layout';
import { Trash2, Upload } from 'lucide-react';

import {
  CoordioDropdown,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@coordio/ui';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { llmService } from '../services/llmService';
import { skillService } from '../services/skillService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';

import type { LlmProviderConfigDto } from '../types/llm';
import type { SkillDto } from '../types/skill';

import './SkillsPage.scss';

/**
 * Quick-filter tabs over the skill's `isActive` dimension.
 *
 * The All tab declares the literal `'all'` because the grid builds each tab's test id as
 * `cordio-datatable-status-tab-<value>`; an empty value emits `cordio-datatable-status-tab-` and
 * GRID-13's `…-status-tab-all` selector matches nothing.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/**
 * Synthetic toolbar key the status tabs write to. Never sent to the API as-is — `'all'` is a UI
 * concept the endpoint has no value for, so {@link toSkillQuery} maps it to `isActive` and drops it
 * entirely for All.
 */
const STATUS_TAB_PARAM_KEY = 'status';

/** Turns the grid's query into the one `skillService.getSkills` accepts. */
function toSkillQuery(query: Record<string, unknown>): Record<string, unknown> {
  const { [STATUS_TAB_PARAM_KEY]: status, ...rest } = query;

  if (status === 'true' || status === 'false') {
    return { ...rest, isActive: status === 'true' };
  }
  return rest;
}

export const SkillsPage = () => {
  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const { showNotification } = useNotificationStore();

  /** Bumped after a mutation to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<SkillDto>();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [providerConfigs, setProviderConfigs] = useState<LlmProviderConfigDto[]>([]);
  const [selectedProviderConfigId, setSelectedProviderConfigId] = useState('');

  /** Only providers whose catalog entry supports hosted skills can receive a bundle. */
  const hostedConfigs = useMemo(
    () => providerConfigs.filter((c) => c.providerSupportsHostedSkills && c.isActive),
    [providerConfigs],
  );

  const tenantId = currentTenant?.id;
  const projectId = currentProject?.id;

  /** A skill bundle belongs to a project, so both halves of the context are required. */
  const contextMissing = !tenantId || !projectId;

  useEffect(() => {
    if (!tenantId) return;
    // Failure here is not fatal to the page: it leaves the upload dialog with no provider options,
    // which it explains on its own.
    llmService.getProviderConfigs().then(setProviderConfigs).catch(() => {});
  }, [tenantId]);

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<SkillDto>((query) => {
        // Unreachable while the guard below renders instead of the grid; throwing rather than
        // silently resolving empty keeps a future regression visible.
        if (contextMissing) throw new Error('No project selected');
        return skillService.getSkills(toSkillQuery(query));
      }),
    // `tenantId` / `projectId` scope the request through the API client's headers rather than the
    // query string, so they still have to invalidate the list. `listVersion` is the deliberate
    // refetch lever: changing the identity of `dataSource` is the documented way to reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, projectId, contextMissing, listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<SkillDto>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '24%' },
      { field: 'slug', header: 'Slug', type: 'text', width: '18%' },
      { field: 'providerName', header: 'Provider', type: 'text', width: '18%' },
      {
        field: 'latestVersionNumber',
        header: 'Version',
        type: 'custom',
        width: '12%',
        // A bare integer would read as a count; the `v` prefix says it is a revision.
        customCell: (row) => <>{row.latestVersionNumber != null ? `v${row.latestVersionNumber}` : '—'}</>,
      },
      { field: 'createdAt', header: 'Uploaded', type: 'date', dateFormat: 'locale', width: '16%' },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '12%',
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search skills by name' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Skill status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [
        {
          key: 'providerId',
          label: 'Provider',
          type: 'select',
          // Lazy: runs the first time the popover opens, never on page load. The endpoint takes a
          // catalog provider id, so the options come from the catalog rather than the row values.
          loadOptions: async () => {
            const providers = await llmService.getProviders(true);
            return providers.map((provider) => ({ label: provider.displayName, value: provider.id }));
          },
        },
        { key: 'category', label: 'Category', type: 'text', placeholder: 'Skill category' },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<SkillDto>>>(
    () => [
      {
        icon: 'trash-2',
        tooltip: 'Delete skill',
        className: 'skills__action--destructive',
        onClick: (row) => openDelete(row),
      },
    ],
    [openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await skillService.deleteSkill(pendingItem.id);
    showNotification('Skill deleted', 'success');
    setListVersion((v) => v + 1);
  }, [pendingItem, showNotification]);

  const handleUpload = useCallback(async () => {
    if (!uploadFile) {
      showNotification('Choose a .zip file containing SKILL.md', 'warning');
      return;
    }
    if (!selectedProviderConfigId) {
      showNotification('Select a target provider', 'warning');
      return;
    }

    setUploading(true);
    try {
      await skillService.createSkillFromZip(uploadFile, uploadCategory || undefined, selectedProviderConfigId);
      showNotification('Skill uploaded', 'success');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadCategory('');
      setSelectedProviderConfigId('');
      setListVersion((v) => v + 1);
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  }, [uploadFile, uploadCategory, selectedProviderConfigId, showNotification]);

  if (contextMissing) {
    return (
      <div className="skills" data-testid="skill-list-page">
        <ShellHeader
          title="Skills"
          description="Skill bundles are uploaded per project and attached to agents from the agent detail page."
          data-testid="skill-list-header"
        />
        <EmptyState
          title="No project selected"
          description="Choose a workspace and project from the switchers above to manage skills."
          data-testid="skill-empty-state"
        />
      </div>
    );
  }

  return (
    <div className="skills" data-testid="skill-list-page">
      <ShellHeader
        title="Skills"
        description="Reusable instruction bundles uploaded to a hosted-skills provider and attachable to any agent."
        data-testid="skill-list-header"
        actions={
          <Button type="button" variant="default" size="sm" className="gap-2" data-testid="add-skill-btn" onClick={() => setUploadOpen(true)}>
            <Upload className="h-4 w-4 shrink-0" aria-hidden />
            Upload skill (zip)
          </Button>
        }
      />

      <div data-testid="skill-list-grid">
        <CordioAbpPagedDataTable<SkillDto>
          className="skills__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
        />
      </div>

      <p className="skills__hint">
        Zip your skill folder — it must contain <strong>SKILL.md</strong> with YAML frontmatter (name,
        description). After upload, open an agent and use its <strong>Skills</strong> tab to attach the skill.
      </p>

      <DeleteDialog
        ref={dialogRef}
        title="Delete skill?"
        entityLabel={pendingItem?.name}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="Every uploaded version of this bundle is removed, and any agent it is attached to loses the skill. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />

      {/* ── Upload dialog ─────────────────────────────────── */}
      <Dialog open={uploadOpen} onOpenChange={(open) => { if (!open && !uploading) setUploadOpen(false); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload skill bundle</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-1">
            <div>
              <label className="mb-1 block text-sm font-medium">Target Provider <span className="text-red-500">*</span></label>
              <CoordioDropdown
                value={selectedProviderConfigId || null}
                onChange={(val) => setSelectedProviderConfigId(val ?? '')}
                options={hostedConfigs.map((c) => ({ label: c.providerName, value: c.id }))}
              />
            </div>
            {hostedConfigs.length === 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                No provider configs with hosted skills support. Configure one under LLM Provider Settings.
              </div>
            )}
            <Button type="button" variant="outline" className="relative" onClick={() => (document.getElementById('skill-zip-input') as HTMLInputElement)?.click()}>
              Choose .zip file
              <input
                id="skill-zip-input"
                type="file"
                accept=".zip,application/zip"
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            {uploadFile && (
              <p className="text-sm text-[var(--color-text-secondary)]">
                {uploadFile.name}
              </p>
            )}
            <div>
              <label className="mb-1 block text-sm font-medium">Category (optional)</label>
              <Input
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={() => void handleUpload()}
              disabled={uploading || !uploadFile || !selectedProviderConfigId}
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
